import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Badge } from "@musa.tech/design-system";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { UserWithPermissions } from "@/types/user";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { maskEmail, maskGeneratorName } from "@/utils/demoMode";
import { useAnalytics } from "@/hooks/useAnalytics";
import { toFormErrorCode } from "@/utils/analyticsErrorCode";
import { ChevronDown, ChevronUp, Eye, EyeOff, X } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn, formatPhoneNumber } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CustomSelect } from "@/components/ui/react-select-custom";
import { PermissionProfilesSelect } from "@/components/Users/PermissionProfilesSelect";
import { ADMIN_PROFILE_KEY } from "@/constants/permissionProfiles";

// Validação de senha robusta conforme regras da API Keycloak
const passwordValidation = z.string().refine(
  (val) => {
    if (!val || val.length === 0) return true; // Vazio é válido aqui (checado depois)
    // Mínimo 8 caracteres
    if (val.length < 8) return false;
    // Pelo menos 1 letra maiúscula
    if (!/[A-Z]/.test(val)) return false;
    // Pelo menos 1 letra minúscula
    if (!/[a-z]/.test(val)) return false;
    // Pelo menos 1 caractere especial
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(val)) return false;
    return true;
  },
  {
    message:
      "A senha deve ter no mínimo 8 caracteres, incluindo: 1 maiúscula, 1 minúscula e 1 caractere especial",
  },
);

// Base schema without password validation
const baseUserFormSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(50),
  lastName: z
    .string()
    .trim()
    .min(2, "Sobrenome deve ter pelo menos 2 caracteres")
    .max(50),
  user_name: z.string().optional(), // Mantido para compatibilidade com edição
  email: z.string().email("Email inválido"),
  phone_number: z
    .string()
    .optional()
    .transform((val) => {
      if (!val || val.trim() === "") return undefined;
      // Remove todos os caracteres não numéricos
      const digits = val.replace(/\D/g, "");
      return digits || undefined;
    })
    .refine(
      (val) => {
        if (!val) return true; // Se vazio, é válido
        return val.length >= 10 && val.length <= 11;
      },
      {
        message: "O telefone deve ter entre 10 e 11 dígitos (incluindo DDD)",
      },
    ),
  permission_role: z.enum(["admin", "standard"]),
  current_password: z.string().optional(),
  new_password: passwordValidation.optional(),
  temp_password: passwordValidation.optional(),
});
type UserFormData = z.infer<typeof baseUserFormSchema>;

interface GeneratorOption {
  id: number;
  name: string;
}

interface EstablishmentOption {
  id: number;
  name: string;
  parent_id: number | null;
  n_level: number | null;
  generator_id?: number | null;
}

interface GeneratorLinkDraft {
  generator_id: number;
  role: "admin" | "standard";
  include_children: boolean;
  establishment_ids: number[];
}

interface UserFormGeneratorLink {
  generator_id: number;
  role: string;
  include_children: boolean;
  establishment_ids?: number[];
}

// Interface para as props do UserForm
export interface UserFormProps {
  user?: UserWithPermissions;
  generatorOptions?: GeneratorOption[];
  establishmentOptions?: EstablishmentOption[];
  onSubmit: (
    data: UserFormData & {
      generator_links?: UserFormGeneratorLink[];
      profiles?: string[];
    },
  ) => void;
  isLoading?: boolean;
  isEditingSelf?: boolean;
  openPasswordSection?: boolean;
  isPasswordRecovery?: boolean;
  /**
   * Troca o par "Operacional / Gestor Admin" pela seleção de perfis de acesso
   * do GR-637. Fica atrás de flag porque o `CreateUserModal` ainda não
   * encadeia o `PUT /users/{id}/profiles` — ligar lá sem isso criaria usuário
   * sem nenhum perfil.
   */
  showPermissionProfiles?: boolean;
  /**
   * Perfis marcados. O estado mora no pai de propósito: o efeito de hidratação
   * deste componente re-dispara quando `generatorOptions`/`establishmentOptions`
   * chegam (assíncronos), e guardar a seleção aqui a apagaria no meio da edição.
   */
  profiles?: string[];
  onProfilesChange?: (next: string[]) => void;
  isLoadingProfiles?: boolean;
  /**
   * Perfis gravados no servidor (só edição). Liga a visualização de diff no
   * seletor — badges "novo"/"será removido" e a linha "Ao salvar".
   */
  profilesBaseline?: string[];
  /**
   * Dados de um usuário excluído/incompleto (MC-91) pra pré-popular a
   * criação. Nunca junto com `user`: reativação é semântica de CRIAÇÃO (senha
   * continua obrigatória, e-mail continua editável) com valores iniciais
   * diferentes — `user` já controla várias outras coisas (edição) que não se
   * aplicam aqui.
   */
  prefillData?: {
    fullname: string | null;
    phone: string | null;
    role: string | null;
    generator_links?: UserFormGeneratorLink[];
  };
  /** Só troca o rótulo do botão — a validação de senha obrigatória já é `!user`. */
  isReactivating?: boolean;
  /** MC-91: dispara a consulta de e-mail ao sair do campo (só faz sentido na criação). */
  onEmailBlur?: (email: string) => void;
}
export const UserForm: React.FC<UserFormProps> = ({
  user,
  generatorOptions = [],
  establishmentOptions = [],
  onSubmit,
  isLoading = false,
  isEditingSelf = false,
  openPasswordSection = false,
  isPasswordRecovery = false,
  showPermissionProfiles = false,
  profiles = [],
  onProfilesChange,
  isLoadingProfiles = false,
  profilesBaseline,
  prefillData,
  isReactivating = false,
  onEmailBlur,
}) => {
  const { isAdmin } = usePermissions();
  const { isDemoMode } = useDemoMode();
  const [isPasswordSectionOpen, setIsPasswordSectionOpen] =
    React.useState(openPasswordSection);
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showTempPassword, setShowTempPassword] = React.useState(false);
  const [generatorLinks, setGeneratorLinks] = React.useState<GeneratorLinkDraft[]>([]);
  const [selectedGeneratorId, setSelectedGeneratorId] = React.useState<string>("");
  const [accessError, setAccessError] = React.useState<string>("");
  const [openManualSections, setOpenManualSections] = React.useState<Record<number, boolean>>({});
  const [establishmentSearch, setEstablishmentSearch] = React.useState<Record<number, string>>({});
  const currentPasswordRef = React.useRef<HTMLInputElement>(null);
  const canManageGeneratorLinks = !user || (isAdmin && !isEditingSelf);

  // Create dynamic schema based on isPasswordRecovery and whether it's a new user
  const userFormSchema = React.useMemo(() => {
    return baseUserFormSchema.refine(
      (data) => {
        // Validação para novo usuário - senha temporária obrigatória
        if (!user && (!data.temp_password || data.temp_password.length < 8)) {
          return false;
        }

        // Se o usuário preencheu a nova senha (edição de senha própria)
        if (data.new_password && data.new_password.length > 0) {
          // Se NÃO é recovery, exigir senha atual
          if (!isPasswordRecovery) {
            if (!data.current_password || data.current_password.length === 0) {
              return false;
            }
          }
          // Validação já é feita pelo passwordValidation
        }
        return true;
      },
      {
        message: !user
          ? "A senha é obrigatória e deve seguir as regras de segurança"
          : isPasswordRecovery
            ? "A nova senha deve seguir as regras de segurança"
            : "Para alterar a senha, preencha a senha atual e a nova senha seguindo as regras de segurança",
        path: !user ? ["temp_password"] : ["new_password"],
      },
    );
  }, [isPasswordRecovery, user]);
  const { trackError } = useAnalytics();
  const form = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      // Se for edição (user existe), separar o nome completo em firstName e lastName
      firstName: user ? user.user_name?.split(" ")[0] || "" : "",
      lastName: user ? user.user_name?.split(" ").slice(1).join(" ") || "" : "",
      user_name: user?.user_name || "", // Mantido para compatibilidade
      email: user?.email || "",
      phone_number: user?.phone_number || "",
      permission_role:
        (user?.permission_role as "admin" | "standard") || "standard",
      temp_password: "",
    },
  });

  // Focus on password field when password section opens
  React.useEffect(() => {
    if (isPasswordSectionOpen && currentPasswordRef.current) {
      // Small delay to wait for collapsible animation
      setTimeout(() => {
        currentPasswordRef.current?.focus();
      }, 300);
    }
  }, [isPasswordSectionOpen]);

  // MC-91: `prefillData` chega depois do mount (clique em "Reativar usuário"),
  // então `defaultValues` (avaliado só uma vez) não alcança — precisa de
  // `form.reset` reativo. Preserva o que já foi digitado (e-mail, senha)
  // espalhando `getValues()` por cima.
  React.useEffect(() => {
    if (!prefillData || user) return;
    const fullname = prefillData.fullname || "";
    form.reset({
      ...form.getValues(),
      firstName: fullname.split(" ")[0] || "",
      lastName: fullname.split(" ").slice(1).join(" ") || "",
      user_name: fullname,
      phone_number: prefillData.phone || "",
      permission_role: (prefillData.role as "admin" | "standard") || "standard",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillData, user]);

  React.useEffect(() => {
    const links = user?.generator_links ?? prefillData?.generator_links;
    if (!links?.length) {
      setGeneratorLinks([]);
      return;
    }

    // Inicializar role global com a role do primeiro vínculo (todas serão iguais)
    const firstRole = links[0].role === "admin" ? "admin" : "standard";
    form.setValue("permission_role", firstRole);

    const grouped = new Map<
      number,
      { role: "admin" | "standard"; include_children: boolean; establishment_ids: number[] }
    >();

    for (const link of links) {
      const role = link.role === "admin" ? "admin" : "standard";

      if (link.include_children) {
        grouped.set(link.generator_id, { role, include_children: true, establishment_ids: [] });
        continue;
      }

      // ID está em generatorOptions → já é N1
      if (generatorOptions.some((g) => g.id === link.generator_id)) {
        if (!grouped.has(link.generator_id)) {
          grouped.set(link.generator_id, { role, include_children: false, establishment_ids: [] });
        }
        continue;
      }

      // É filho → resolve N1 via generator_id do establishment
      const est = establishmentOptions.find((e) => e.id === link.generator_id);
      const n1Id = est?.generator_id ?? null;

      if (n1Id != null) {
        const entry = grouped.get(n1Id);
        if (entry) {
          entry.establishment_ids.push(link.generator_id);
        } else {
          grouped.set(n1Id, { role, include_children: false, establishment_ids: [link.generator_id] });
        }
      }
    }

    setGeneratorLinks(
      Array.from(grouped.entries()).map(([generator_id, data]) => ({
        generator_id,
        ...data,
      })),
    );
  }, [user, prefillData, establishmentOptions, generatorOptions]);

  /**
   * Validação do zod reprovada. Registra o código do **campo**, nunca a mensagem
   * de validação nem o valor digitado (RN-05).
   */
  const handleInvalid = (errors: Record<string, unknown>) => {
    trackError({
      errorType: "form_error",
      errorCode: toFormErrorCode(Object.keys(errors)),
    });
  };

  const handleSubmit = (data: UserFormData) => {
    if (canManageGeneratorLinks) {
      // Estas duas travas rodam DEPOIS da validação do zod. Sem registrá-las, o
      // funil mostraria pessoas que confirmaram e desapareceram (GR-712).
      if (generatorLinks.length === 0) {
        trackError({ errorType: "form_error", errorCode: "sem_gerador_vinculado" });
        setAccessError("Selecione ao menos um gerador.");
        return;
      }

      const hasInvalidManualSelection = generatorLinks.some(
        (link) => !link.include_children && link.establishment_ids.length === 0,
      );

      if (hasInvalidManualSelection) {
        trackError({
          errorType: "form_error",
          errorCode: "sem_estabelecimento_selecionado",
        });
        setAccessError("Selecione ao menos um estabelecimento para os vínculos em seleção manual.");
        return;
      }
    }

    // Achado em review: com os perfis ainda em esqueleto, `profiles` está
    // vazio — salvar nesse instante derivaria globalRole="standard" e
    // rebaixaria qualquer admin, mesmo que ninguém tenha tocado em nada.
    if (showPermissionProfiles && isLoadingProfiles) {
      trackError({ errorType: "form_error", errorCode: "perfis_carregando" });
      setAccessError("Aguarde os perfis de acesso carregarem antes de salvar.");
      return;
    }

    // GR-637: com os perfis na tela, `permission_role` deixa de ser escolhido à
    // mão e passa a ser derivado do perfil `admin`. O campo continua existindo
    // e sendo enviado porque `canManageUsers` (useUnifiedAuth.ts:167-170), o
    // menu e a guarda de `/usuarios` ainda leem `role`, não grupo — o épico
    // GR-561 manda manter os dois em paralelo até a Onda 1 ligar a leitura de
    // grupo no token. Derivar (em vez de esconder) evita o estado incoerente
    // "perfil admin marcado, role standard".
    const globalRole: "admin" | "standard" = showPermissionProfiles
      ? profiles.includes(ADMIN_PROFILE_KEY)
        ? "admin"
        : "standard"
      : data.permission_role;

    const generatorLinksPayload = canManageGeneratorLinks
      ? generatorLinks.flatMap((link) => {
          if (link.include_children) {
            return [{
              generator_id: link.generator_id,
              role: globalRole,
              include_children: true,
            }];
          }
          // Seleção manual: uma entrada por filho selecionado, sem o N1
          return link.establishment_ids.map((childId) => ({
            generator_id: childId,
            role: globalRole,
            include_children: false,
          }));
        })
      : undefined;

    onSubmit({
      ...data,
      permission_role: globalRole,
      ...(generatorLinksPayload ? { generator_links: generatorLinksPayload } : {}),
      ...(showPermissionProfiles ? { profiles } : {}),
    });
  };

  const availableGenerators = React.useMemo(
    () =>
      generatorOptions.filter(
        (generator) =>
          !generatorLinks.some((link) => link.generator_id === generator.id),
      ),
    [generatorLinks, generatorOptions],
  );

  const getGeneratorName = React.useCallback(
    (generatorId: number) => {
      const name = generatorOptions.find((generator) => generator.id === generatorId)?.name;
      return maskGeneratorName(name, isDemoMode) || `Gerador ${generatorId}`;
    },
    [generatorOptions, isDemoMode],
  );

  const establishmentsByParent = React.useMemo(() => {
    return establishmentOptions.reduce<Record<number, EstablishmentOption[]>>(
      (acc, establishment) => {
        if (establishment.parent_id == null) return acc;
        if (!acc[establishment.parent_id]) {
          acc[establishment.parent_id] = [];
        }
        acc[establishment.parent_id].push(establishment);
        return acc;
      },
      {},
    );
  }, [establishmentOptions]);

  const getDirectChildren = React.useCallback(
    (parentId: number) =>
      [...(establishmentsByParent[parentId] ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [establishmentsByParent],
  );

  const getDescendantIds = React.useCallback(
    (parentId: number): number[] => {
      const walk = (currentParentId: number): number[] => {
        const children = establishmentsByParent[currentParentId] ?? [];

        return children.flatMap((child) => [child.id, ...walk(child.id)]);
      };

      return walk(parentId);
    },
    [establishmentsByParent],
  );

  const addGeneratorLink = (value: string) => {
    const generatorId = parseInt(value, 10);
    if (isNaN(generatorId)) return;

    setGeneratorLinks((prev) => [
      ...prev,
      {
        generator_id: generatorId,
        role: form.getValues("permission_role") as "admin" | "standard",
        include_children: true,
        establishment_ids: [],
      },
    ]);
    setOpenManualSections((prev) => ({ ...prev, [generatorId]: true }));
    setSelectedGeneratorId("");
    setAccessError("");
  };

  const removeGeneratorLink = (generatorId: number) => {
    setGeneratorLinks((prev) =>
      prev.filter((link) => link.generator_id !== generatorId),
    );
    setOpenManualSections((prev) => {
      const next = { ...prev };
      delete next[generatorId];
      return next;
    });
    setEstablishmentSearch((prev) => {
      const next = { ...prev };
      delete next[generatorId];
      return next;
    });
  };

  const updateGeneratorLink = (
    generatorId: number,
    updater: (current: GeneratorLinkDraft) => GeneratorLinkDraft,
  ) => {
    setGeneratorLinks((prev) =>
      prev.map((link) =>
        link.generator_id === generatorId ? updater(link) : link,
      ),
    );
    setAccessError("");
  };

  // Determine available role options based on current user's role
  const availableRoleOptions = React.useMemo(() => {
    const baseOptions = [
      {
        value: "standard",
        label: "Operacional",
      },
      {
        value: "admin",
        label: "Gestor Admin",
      },
    ];

    return baseOptions;
  }, []);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
        className="space-y-4"
      >
        {/* Nome e Sobrenome para todos os casos */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome *</FormLabel>
                <FormControl>
                  <Input placeholder="Nome" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sobrenome *</FormLabel>
                <FormControl>
                  <Input placeholder="Sobrenome" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="email@exemplo.com"
                  {...field}
                  value={
                    user ? maskEmail(field.value, isDemoMode) : field.value
                  }
                  disabled={!!user}
                  onBlur={(e) => {
                    field.onBlur();
                    onEmailBlur?.(e.target.value);
                  }}
                />
              </FormControl>
              {user && (
                <p className="text-sm text-gray-500">
                  O email não pode ser alterado
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Password Field (only for new users) */}
        {!user && (
          <FormField
            control={form.control}
            name="temp_password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Senha *</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showTempPassword ? "text" : "password"}
                      placeholder="Digite a senha"
                      {...field}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowTempPassword(!showTempPassword)}
                    >
                      {showTempPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <p className="text-xs text-gray-500 mt-1">
                  Mínimo 8 caracteres, incluindo: 1 maiúscula, 1 minúscula e 1
                  caractere especial
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="phone_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Telefone (Opcional)</FormLabel>
              <FormControl>
                <Input
                  placeholder="(11) 99999-9999"
                  {...field}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    field.onChange(formatted);
                  }}
                  maxLength={15}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {canManageGeneratorLinks && (
          <div className="space-y-4">
            {showPermissionProfiles ? (
              <div className="space-y-2">
                <FormLabel>Perfis de acesso</FormLabel>
                <p className="text-sm text-muted-foreground">
                  Os perfis somam. Marque quantos a pessoa precisar — cada um
                  mostra o que libera.
                </p>
                <PermissionProfilesSelect
                  value={profiles}
                  onChange={(next) => onProfilesChange?.(next)}
                  disabled={isLoading}
                  isLoading={isLoadingProfiles}
                  baseline={profilesBaseline}
                />
              </div>
            ) : (
              <FormField
                control={form.control}
                name="permission_role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Perfil de acesso</FormLabel>
                    <div className="grid grid-cols-2 gap-2">
                      {availableRoleOptions.map((option) => (
                        <Button
                          key={option.value}
                          type="button"
                          variant="outline"
                          onClick={() => field.onChange(option.value)}
                          className={
                            field.value === option.value
                              ? "bg-primary-500 text-white border-primary-50 hover:bg-primary-600 hover:text-white"
                              : ""
                          }
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </FormItem>
                )}
              />
            )}

            <div className="space-y-2">
              <FormLabel>Gerador</FormLabel>
              <p className="text-sm text-gray-500">
                Selecione os geradores e estabelecimentos aos quais o usuário terá acesso
              </p>
              <CustomSelect
                options={availableGenerators.map((generator) => ({
                  value: generator.id.toString(),
                  label: maskGeneratorName(generator.name, isDemoMode),
                }))}
                value={selectedGeneratorId}
                onChange={(value) => {
                  setSelectedGeneratorId(value);
                  addGeneratorLink(value);
                }}
                placeholder="Buscar gerador N1..."
                className="w-full"
              />
            </div>

            {generatorLinks.map((link) => {
              const topLevelEstablishments = getDirectChildren(link.generator_id);

              const renderLevel = (
                parentId: number,
                fallbackLevel: number,
              ): React.ReactNode => {
                const establishments = getDirectChildren(parentId);
                if (establishments.length === 0) return null;
                const level = establishments[0]?.n_level ?? fallbackLevel;

                const searchTerm =
                  establishmentSearch[parentId]?.trim().toLowerCase() || "";
                const filteredEstablishments = establishments.filter(
                  (establishment) =>
                    establishment.name.toLowerCase().includes(searchTerm),
                );

                const selectedParentsWithChildren = establishments.filter(
                  (establishment) =>
                    link.establishment_ids.includes(establishment.id) &&
                    getDirectChildren(establishment.id).length > 0,
                );

                return (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex min-w-8 items-center justify-center rounded-full border border-primary/20 bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                        N{level}
                      </span>
                      <span className="text-sm font-medium text-gray-700">
                        Selecione os geradores do nível {level}
                      </span>
                    </div>

                    <Input
                      value={establishmentSearch[parentId] || ""}
                      onChange={(event) =>
                        setEstablishmentSearch((prev) => ({
                          ...prev,
                          [parentId]: event.target.value,
                        }))
                      }
                      placeholder={`Buscar geradores N${level}...`}
                    />

                    <div className="space-y-2 rounded-md border p-3 max-h-48 overflow-y-auto">
                      {filteredEstablishments.length > 0 ? (
                        filteredEstablishments.map((establishment) => {
                          const checked = link.establishment_ids.includes(
                            establishment.id,
                          );

                          return (
                            <label
                              key={establishment.id}
                              className="flex items-start gap-3 cursor-pointer"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(value) => {
                                  updateGeneratorLink(
                                    link.generator_id,
                                    (current) => {
                                      if (value === true) {
                                        return {
                                          ...current,
                                          establishment_ids:
                                            current.establishment_ids.includes(
                                              establishment.id,
                                            )
                                              ? current.establishment_ids
                                              : [
                                                  ...current.establishment_ids,
                                                  establishment.id,
                                                ],
                                        };
                                      }

                                      const idsToRemove = [
                                        establishment.id,
                                        ...getDescendantIds(establishment.id),
                                      ];

                                      return {
                                        ...current,
                                        establishment_ids:
                                          current.establishment_ids.filter(
                                            (id) => !idsToRemove.includes(id),
                                          ),
                                      };
                                    },
                                  );
                                }}
                              />
                              <span className="flex items-start gap-2 text-sm">
                                <span className="inline-flex min-w-8 items-center justify-center rounded-full border border-primary/20 bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                                  N{level}
                                </span>
                                <span className="flex flex-col">
                                  <span>{establishment.name}</span>
                                  <span className="text-xs text-gray-500">
                                    ID: {establishment.id}
                                  </span>
                                </span>
                              </span>
                            </label>
                          );
                        })
                      ) : (
                        <div className="text-sm text-gray-500">
                          Nenhum gerador encontrado
                        </div>
                      )}
                    </div>

                    {selectedParentsWithChildren.map((establishment) => (
                      <div
                        key={`children-${establishment.id}`}
                      >
                        {renderLevel(
                          establishment.id,
                          establishment.n_level != null
                            ? establishment.n_level + 1
                            : level + 1,
                        )}
                      </div>
                    ))}
                  </div>
                );
              };

              return (
                <div key={link.generator_id} className="rounded-lg border border-gray-200 p-4 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        <span className="inline-flex min-w-8 items-center justify-center rounded-full border border-primary/20 bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                          N1
                        </span>
                        <span>{getGeneratorName(link.generator_id)}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {link.generator_id}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => removeGeneratorLink(link.generator_id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        updateGeneratorLink(link.generator_id, (current) => ({
                          ...current,
                          include_children: true,
                          establishment_ids: [],
                        }))
                      }
                      className={link.include_children ? "bg-primary-500 text-white border-primary-50 hover:bg-primary-600 hover:text-white" : ""}
                    >
                      Todos os níveis
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        updateGeneratorLink(link.generator_id, (current) => ({
                          ...current,
                          include_children: false,
                        }))
                      }
                      className={!link.include_children ? "bg-primary-500 text-white border-primary-50 hover:bg-primary-600 hover:text-white" : ""}
                    >
                      Seleção manual
                    </Button>
                  </div>

                  {link.include_children ? (
                    <div className="text-sm text-gray-600">
                      Acesso completo aos estabelecimentos desse gerador.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Collapsible
                        open={openManualSections[link.generator_id] ?? true}
                        onOpenChange={(open) =>
                          setOpenManualSections((prev) => ({
                            ...prev,
                            [link.generator_id]: open,
                          }))
                        }
                      >
                        <CollapsibleTrigger asChild>
                          <button
                            type="button"
                            className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-gray-50"
                          >
                            <span>Estabelecimentos vinculados</span>
                            {openManualSections[link.generator_id] ?? true ? (
                              <ChevronUp className="h-4 w-4 text-gray-500" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            )}
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-3">
                          {topLevelEstablishments.length > 0 ? (
                            renderLevel(
                              link.generator_id,
                              topLevelEstablishments[0]?.n_level ?? 2,
                            )
                          ) : (
                            <div className="text-sm text-gray-500">
                              Nenhum estabelecimento encontrado
                            </div>
                          )}
                        </CollapsibleContent>
                      </Collapsible>

                      <div className="text-sm text-gray-600">
                        {link.establishment_ids.length} selecionado
                        {link.establishment_ids.length === 1 ? "" : "s"}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {accessError && (
              <p className="text-sm text-destructive">{accessError}</p>
            )}
          </div>
        )}

        {isEditingSelf && (
          <div className="mt-6 border rounded-lg">
            <Collapsible
              open={isPasswordSectionOpen}
              onOpenChange={setIsPasswordSectionOpen}
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">Alterar Senha</span>
                    <span className="text-xs text-gray-500">(Opcional)</span>
                  </div>
                  {isPasswordSectionOpen ? (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4 space-y-4">
                <div className="pt-2 border-t">
                  {isPasswordRecovery ? (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Redefinição de senha:</strong> Escolha uma nova
                        senha segura para sua conta.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 mb-4">
                      Para alterar sua senha, preencha os campos abaixo com sua
                      senha atual e a nova senha desejada.
                    </p>
                  )}

                  {!isPasswordRecovery && (
                    <FormField
                      control={form.control}
                      name="current_password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Senha Atual</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showCurrentPassword ? "text" : "password"}
                                placeholder="Digite sua senha atual"
                                {...field}
                                ref={currentPasswordRef}
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setShowCurrentPassword(!showCurrentPassword)
                                }
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                              >
                                {showCurrentPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="new_password"
                    render={({ field }) => (
                      <FormItem className={isPasswordRecovery ? "" : "mt-3"}>
                        <FormLabel>Nova Senha</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showNewPassword ? "text" : "password"}
                              placeholder="Digite a nova senha (mínimo 6 caracteres)"
                              {...field}
                              ref={
                                isPasswordRecovery
                                  ? currentPasswordRef
                                  : undefined
                              }
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              {showNewPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            // O handleSubmit já recusa nesse estado (guard acima) — o botão
            // visualmente desabilitado é o reforço, não a única trava.
            disabled={isLoading || (showPermissionProfiles && isLoadingProfiles)}
          >
            {isLoading
              ? "Salvando..."
              : showPermissionProfiles && isLoadingProfiles
                ? "Carregando perfis..."
                : user
                  ? "Salvar Alterações"
                  : isReactivating
                    ? "Reativar usuário"
                    : "Criar Usuário"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
